import { int } from "@goodgamestudios/math-utilities";

import Point = createjs.Point;

/**
 * Allows for Objects to follow a 2D bezier path. This path is made from a list of
 * points these points are either control points or through points for the bezier.
 */
export class Bezier2D {

    /**
     * The Object you wish to be updated with x,y and rotation values.
     */
    public item: any;

    /**
     * A list of Points defining nodes of the bezier path.
     */
    private _pts: any[];

    /**
     * Whether the Object will directionally rotate.
     */
    public autoRotate: boolean = false;

    /**
     * Whether the pts in the bezier path will be used as control points or through points.
     */
    public through: boolean = false;

    /**
     * Whether the pts in the BezierPath can be repositioned.
     */
    public movingPts: boolean = false;

    private curve: any[];
    private _position: number = 0;
    private _distance: number = 0;
    private radsDegree: number = 180 / Math.PI;

    /**
     * @param item The Object you wish to be updated with x,y and rotation values.
     * @param through Whether the pts in the bezier path will be used as control points or through points.
     * @param autoRotate Whether the Object will directionally rotate.
     * @param movingPts Whether the pts in the BezierPath can be repositioned.
     */
    constructor(item: any, through: boolean = false, autoRotate: boolean = false, movingPts: boolean = false, ...pts: any[]) {
        this.item = item;
        this.through = through;
        this.autoRotate = autoRotate;
        this.movingPts = movingPts;
        this._pts = pts;
        this.curve = [];
    }

    public set path(curve: any[]) {
        this._pts = curve;
        this.update();
    }

    public get path(): any[] {
        return this._pts;
    }

    /**
     * @param index The position to return a Point from.
     * @return Returns a Point at the specified index.
     */
    public index(index: number): Point {
        return this._pts[index];
    }

    /**
     * Pushes a Point into the Bezier path.
     * @param pt The Point which will be added to the end of the bezier path.
     */
    public push(pt: Point): void {
        this._pts.push(pt);
        this.update();
    }

    /**
     * Adds a Point into the Bezier path at the specified index.
     * @param index The index for which to insert the Point.
     * @param pt The Point to insert at the index.
     */
    public addAt(index: number, pt: Point): void {
        this._pts.splice(index, 0, pt);
        this.update();
    }

    /**
     * Removes a Point from the Bezier path.
     * @param pt The Point you would like to remove from the bezier path.
     */
    public remove(pt: Point): void {
        var index: number = int(this._pts.indexOf(pt));
        if (index != -1) this.removeAt(index, 1);
    }

    /**
     * Removes one or more Point(s) at the specified index and count from the Bezier path.
     * @param index The index to start removing nodes from the Bezier path.
     * @param count the Number of nodes to remove from the Bezier path. If this is 0 no nodes are removed.
     */
    public removeAt(index: number, count: number = 1): void {
        this._pts.splice(index, count);
        this.update();
    }

    /**
     * @return The number of nodes in the Bezier path.
     */
    public get length(): number {
        return this._pts.length;
    }

    /**
     * Sets the position on the Object along the bezier path from 0-1.
     * @param num The position from 0-1.
     */
    public set position(num: number) {
        this._position = num;

        if (this.curve.length == 0) return;

        if (this.movingPts) this.update();

        var posDist: number = num * this._distance;
        var distance: number = 0;
        var arcDist: number = NaN;
        var index: number = 0;
        var segments: number = int(this.curve.length - 3);

        for (index = 0; index <= segments; index += 2) {
            arcDist = this.arcLength(this.curve[index], this.curve[index + 1], this.curve[index + 2]);

            if (distance + arcDist > posDist) break;

            distance += arcDist;
        }

        index = (index > segments) ? segments : index;
        var inc: number = (posDist == this._distance) ? 1 : (posDist - distance) / arcDist;

        var sPt: Point = this.curve[index];
        var cPt: Point = this.curve[index + 1];
        var ePt: Point = this.curve[index + 2];

        var pt: Point = new Point(this.quadratic(inc, sPt.x, cPt.x, ePt.x), this.quadratic(inc, sPt.y, cPt.y, ePt.y));
        this.item.x = pt.x;
        this.item.y = pt.y;

        if (this.autoRotate) this.item.rotation = this.angle(inc, sPt, cPt, ePt) * this.radsDegree;
    }

    /**
     * @return The current position of the Object along the Bezier path from 0-1.
     */
    public get position(): number {
        return this._position;
    }

    private update(): void {
        if (this._pts.length <= 2) return;

        var i: number = 0;
        var len: number = 0;
        this.curve = [];

        if (this.through) {
            var pt: Point = this._pts[int(0)];
            var cPt: Point;

            this.curve.push(pt);

            cPt = this.index(2).subtract(pt);
            cPt.x /= 4;
            cPt.y /= 4;
            cPt = this.index(1).subtract(cPt);

            pt = this.index(1);

            this.curve.push(cPt);
            this.curve.push(pt);

            len = int(this._pts.length - 1);

            for (i = 1; i < len; i++) {
                cPt = this.index(i).add(this.index(i).subtract(cPt));
                pt = this.index(i + 1);

                this.curve.push(cPt);
                this.curve.push(pt);
            }
        } else {
            len = int(3 + int((this._pts.length - 3) / 2) * 2);

            for (i = 0; i < len; i++) {
                this.curve.push(this.index(i));
            }
        }

        this.updateDistance();
    }

    private updateDistance(): void {
        this._distance = 0;
        for (var i: number = 0; i <= this.curve.length - 3; i += 2) {
            this._distance += this.arcLength(this.curve[i], this.curve[i + 1], this.curve[i + 2]);
        }
    }

    private arcLength(pt1: Point, pt2: Point, pt3: Point): number {

        var a1: number = this.angleBetween(pt1, pt2);
        var a2: number = this.angleBetween(pt2, pt3);
        if (a1 == a2 || (a2 == a1 + Math.PI)) return this.distanceBetween(pt1, pt3);

        var vmDivisor: number = (pt2.y - .5 * pt3.y - .5 * pt1.y);
        var vm: number = 0;
        if (vmDivisor != 0) {
            vm = -(pt2.x - .5 * pt3.x - .5 * pt1.x) / vmDivisor;
        }
        var vtDivisor: number = (vm * (pt1.x - 2 * pt2.x + pt3.x) - (pt1.y - 2 * pt2.y + pt3.y));
        var vt: number = 0;
        if (vtDivisor != 0) {
            vt = (-vm * (pt2.x - pt1.x) + (pt2.y - pt1.y)) / vtDivisor;
        }

        var va: number = this.angle(vt, pt1, pt2, pt3);

        var ra: number = this.projectDistance(pt1, pt2);
        var rb: number = this.projectDistance(pt2, pt3);
        var alfa: number = va - this.radAngle(pt1, pt2);

        var beta: number = this.radAngle(pt2, pt3) - va;
        var rax: number = ra * Math.cos(alfa);
        var ray: number = ra * Math.sin(alfa);

        var rbx: number = rb * Math.cos(beta);
        var rby: number = rb * Math.sin(beta);

        var l1: number = .5 * this.parabolaArcLength(ray, 2 * Math.abs(rax));
        var l2: number = .5 * this.parabolaArcLength(rby, 2 * Math.abs(rbx));

        return (rax < 0 || rbx < 0) ? Math.abs(l2 - l1) : l1 + l2;
    }

    private angleBetween(p1: Point, p2: Point): number {
        var x: number = p2.x - p1.x;
        var y: number = p2.y - p1.y;

        return Math.atan2(y, x);
    }

    private distanceBetween(p1: Point, p2: Point): number {
        var x: number = p2.x - p1.x;
        var y: number = p2.y - p1.y;

        return Math.sqrt((x * x) + (y * y));
    }

    private segment(pt1: Point, pt2: Point, pt3: Point, t1: number, t2: number): any {
        var b1: Point = new Point();
        var b2: Point = new Point();
        var b3: Point = new Point();

        b1.x = this.quadratic(t1, pt1.x, pt2.x, pt3.x);
        b1.y = this.quadratic(t1, pt1.y, pt2.y, pt3.y);
        b3.x = this.quadratic(t2, pt1.x, pt2.x, pt3.x);
        b3.y = this.quadratic(t2, pt1.y, pt2.y, pt3.y);
        b2.x = this.control(t1, t2, pt1.x, pt2.x, pt3.x);
        b2.y = this.control(t1, t2, pt1.y, pt2.y, pt3.y);

        return { b1: b1, b2: b2, b3: b3 };
    }/*;*/ private control(t1: number, t2: number, a: number, b: number, c: number): number {
        return a + (t1 + t2) * (b - a) + t1 * t2 * (c - 2 * b + a);
    }

    private radAngle(p1: Point, p2: Point): number {
        return Math.atan2(p2.y - p1.y, p2.x - p1.x);
    }

    private projectDistance(p1: Point, p2: Point): number {
        return Math.sqrt(p2.x * p2.x + p1.x * p1.x - 2 * p1.x * p2.x + p2.y * p2.y + p1.y * p1.y - 2 * p1.y * p2.y);
    }

    private parabolaArcLength(a: number, b: number): number {
        if (a == 0) return 0;

        return .5 * Math.sqrt(b * b + 16 * a * a) + ((b * b) / (8 * a)) * Math.log((4 * a + Math.sqrt(b * b + 16 * a * a)) / b);
    }

    private angle(t: number, pt1: Point, pt2: Point, pt3: Point): number {
        return (Math.atan2(this.derivative(t, pt1.y, pt2.y, pt3.y), this.derivative(t, pt1.x, pt2.x, pt3.x)));
    }

    private quadratic(t: number, a: number, b: number, c: number): number {
        return a + t * (2 * (1 - t) * (b - a) + t * (c - a));
    }

    private derivative(t: number, a: number, b: number, c: number): number {
        return 2 * a * (t - 1) + 2 * b * (1 - 2 * t) + 2 * c * t;
    }
}
